import { buildGradientCss, tripletToHex, hexToTriplet } from "@/lib/appearance";
import { GRADIENT_KEYS } from "@/lib/appearance";
import { Slider } from "@/components/ui/slider";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";

function ColorField({ label, value, onChange, testid }) {
  return (
    <div className="flex items-center gap-2">
      <input type="color" value={value || "#000000"} onChange={(e) => onChange(e.target.value)}
        className="h-8 w-8 shrink-0 cursor-pointer rounded border border-border bg-transparent p-0"
        data-testid={testid} />
      <span className="text-[11px] font-medium text-muted-foreground">{label}</span>
    </div>
  );
}

export function GradientEditor({ gradients, onChange }) {
  const g = gradients || {};
  const update = (key, patch) => onChange({ ...g, [key]: { ...(g[key] || {}), ...patch } });

  return (
    <div className="space-y-4" data-testid="gradient-editor">
      {GRADIENT_KEYS.map(({ key, label, desc }) => {
        const grad = g[key] || { from: "#142d21", to: "#0f1a14", angle: 135, opacity: 1, intensity: 0.7 };
        const hasMid = grad.mid != null && grad.mid !== "";
        return (
          <div key={key} className="rounded-lg border border-border p-3" data-testid={`gradient-${key}`}>
            <div className="mb-2 flex items-center justify-between">
              <div>
                <div className="text-sm font-medium text-foreground">{label}</div>
                <div className="text-[11px] text-muted-foreground">{desc}</div>
              </div>
            </div>
            <div className="mb-3 h-8 w-full rounded-md border border-black/5"
              style={{ backgroundImage: buildGradientCss(grad) }} />
            <div className="flex flex-wrap items-center gap-3">
              <ColorField label="Start" value={grad.from} onChange={(v) => update(key, { from: v })} testid={`grad-${key}-from`} />
              <div className="flex items-center gap-1.5">
                <Switch checked={hasMid} onCheckedChange={(c) => update(key, { mid: c ? (grad.from) : null })} data-testid={`grad-${key}-mid-toggle`} />
                <span className="text-[11px] text-muted-foreground">Middle</span>
              </div>
              {hasMid && <ColorField label="Mid" value={grad.mid} onChange={(v) => update(key, { mid: v })} testid={`grad-${key}-mid`} />}
              <ColorField label="End" value={grad.to} onChange={(v) => update(key, { to: v })} testid={`grad-${key}-to`} />
            </div>
            <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-3">
              <div>
                <Label className="text-[11px] text-muted-foreground">Angle · {grad.angle ?? 135}°</Label>
                <Slider min={0} max={360} step={5} value={[grad.angle ?? 135]} onValueChange={([v]) => update(key, { angle: v })} className="mt-2" />
              </div>
              <div>
                <Label className="text-[11px] text-muted-foreground">Opacity · {Math.round((grad.opacity ?? 1) * 100)}%</Label>
                <Slider min={0} max={100} step={5} value={[(grad.opacity ?? 1) * 100]} onValueChange={([v]) => update(key, { opacity: v / 100 })} className="mt-2" />
              </div>
              <div>
                <Label className="text-[11px] text-muted-foreground">Intensity · {Math.round((grad.intensity ?? 0.7) * 100)}%</Label>
                <Slider min={0} max={100} step={5} value={[(grad.intensity ?? 0.7) * 100]} onValueChange={([v]) => update(key, { intensity: v / 100 })} className="mt-2" />
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

export { ColorField };
